/**
 * Profile Service
 * localforage is the PRIMARY store; the optional Billy-hosted sync API
 * (src/services/syncService.js) is a best-effort mirror for multi-device use.
 * Absence of VITE_SYNC_URL degrades gracefully to purely-local operation.
 */

import localforage from 'localforage';
import { v4 as uuidv4 } from 'uuid';
import errorLog from './errorLog';
import {
    isSyncEnabled,
    mergeProfiles,
    pullAll,
    pushProfile as syncPush,
    deleteProfile as syncDelete
} from './syncService';

// Configure localforage
localforage.config({
    name: 'LooperMIDIControl',
    storeName: 'profiles'
});

class ProfileService {
    /**
     * Get all profiles (local store merged with the cloud mirror, newest wins).
     */
    async getAllProfiles() {
        try {
            let profiles = await this._getAllProfilesLocal();

            if (isSyncEnabled) {
                try {
                    const remote = await pullAll();
                    const { merged, remoteWonIds } = mergeProfiles(profiles, Object.values(remote));
                    // Persist remote winners so the next offline start still sees them.
                    for (const id of remoteWonIds) {
                        const winner = merged.find(p => p.id === id);
                        if (winner) await this._saveProfileLocal(winner);
                    }
                    profiles = merged;
                } catch (error) {
                    console.warn('[profiles] cloud pull failed; using local only:', error.message);
                    errorLog.capture(error, 'profile', { op: 'syncPull' });
                }
            }

            // SORT: Newest first
            profiles.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));

            // DEDUPLICATION & MIGRATION: Strict "Default Setup" cleanup
            const defaultSetups = profiles.filter(p => p.name === 'Default Setup');
            if (defaultSetups.length > 0) {
                // 1. Sort by newest
                defaultSetups.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));

                // 2. Keep only the most recent one
                const keeper = defaultSetups[0];
                const duplicates = defaultSetups.slice(1);

                if (duplicates.length > 0) {
                    console.log(`🧹 Found ${duplicates.length} duplicate "Default Setup" profiles. Removing...`);
                    profiles = profiles.filter(p => p.name !== 'Default Setup' || p.id === keeper.id);
                    // Tolerate individual delete failures — don't let cleanup
                    // abort loading the user's whole profile list.
                    for (const dup of duplicates) {
                        try {
                            await this.deleteProfile(dup.id);
                        } catch (error) {
                            console.warn(`Could not delete duplicate profile ${dup.id}; will retry on next load.`, error);
                        }
                    }
                }

                // 3. MIGRATION: Ensure key settings match the latest "Gold Standard" defaults
                // The user expects: Ext1 = change_track_focus, Ext2 = mute_current_track
                let needsUpdate = false;

                // Check Ext1
                if (keeper.footswitches?.ext1?.function !== 'change_track_focus') {
                    keeper.footswitches.ext1 = { function: 'change_track_focus', holdFunction: null, enabled: true };
                    needsUpdate = true;
                }

                // Check Ext2
                if (keeper.footswitches?.ext2?.function !== 'mute_current_track') {
                    keeper.footswitches.ext2 = { function: 'mute_current_track', holdFunction: null, enabled: true };
                    needsUpdate = true;
                }

                // Check Encoder
                if (keeper.encoder?.function !== 'fade_time') {
                    keeper.encoder = { function: 'fade_time' };
                    needsUpdate = true;
                }

                // Check MIDI Channel (Default should be 1)
                if (keeper.midiSettings?.channel !== 1) {
                    keeper.midiSettings.channel = 1;
                    needsUpdate = true;
                }

                if (needsUpdate) {
                    console.log('🔄 Migrating "Default Setup" to latest defaults...');
                    await this.saveProfile(keeper);
                }
            }

            return profiles;

        } catch (error) {
            console.error('Error in getAllProfiles:', error);
            errorLog.capture(error, 'profile', { op: 'getAllProfiles' });
            return [];
        }
    }

    async _getAllProfilesLocal() {
        const keys = await localforage.keys();
        const profiles = [];

        for (const key of keys) {
            const profile = await localforage.getItem(key);
            profiles.push(profile);
        }

        return profiles;
    }

    /**
     * Save profile locally first (source of truth), then mirror to the cloud.
     */
    async saveProfile(profile) {
        const profileToSave = {
            ...profile,
            id: profile.id || uuidv4(),
            updatedAt: new Date().toISOString()
        };

        if (!profileToSave.createdAt) {
            profileToSave.createdAt = profileToSave.updatedAt;
        }

        await this._saveProfileLocal(profileToSave);

        if (isSyncEnabled) {
            const pushed = await syncPush(profileToSave);
            if (!pushed) {
                console.warn(`[profiles] cloud mirror missed for ${profileToSave.id}; local save stands.`);
            }
        }

        return profileToSave;
    }

    async _saveProfileLocal(profile) {
        await localforage.setItem(profile.id, profile);
    }

    /**
     * Delete profile from the local store (throws on failure so callers can
     * revert optimistic UI); the cloud mirror delete is best-effort.
     */
    async deleteProfile(profileId) {
        await this._deleteProfileLocal(profileId);
        if (isSyncEnabled) {
            await syncDelete(profileId);
        }
    }

    async _deleteProfileLocal(profileId) {
        await localforage.removeItem(profileId);
    }

    /**
     * Create default profile
     */
    createDefaultProfile() {
        return {
            id: uuidv4(),
            name: 'Default Setup',
            deviceType: 'looper-plus',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            footswitches: {
                fs1: {
                    function: 'rec_play',
                    holdFunction: null,
                    enabled: true
                },
                fs2: {
                    function: 'stop',
                    holdFunction: null,
                    enabled: true
                },
                ext1: {
                    function: 'change_track_focus',
                    holdFunction: null,
                    enabled: true
                },
                ext2: {
                    function: 'mute_current_track',
                    holdFunction: null,
                    enabled: true
                }
            },
            encoder: {
                function: 'fade_time'
            },
            midiSettings: {
                channel: 1,
                clockEnabled: false,
                inputSource: 'usb',
                externalControl: true
            },
            trackSettings: {
                mode: 'serial',
                recordingLogic: 'auto',
                overdubMode: 'overdub'
            },
            loopSlots: {}
        };
    }
}

// Export both the service instance AND the function
export const profileService = new ProfileService();
export const createDefaultProfile = () => profileService.createDefaultProfile();
export default profileService;
